import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { IconRides } from '../components/icons';
import { Chips, DataTable, Empty, ErrorNote, Loading, PageHead, Pager, RideStatusPill, RowCard, VehicleTile, type Column } from '../components/ui';
import { api } from '../lib/api';
import { dateTime, km, phone, rupees, shortId, VEHICLE_LABEL } from '../lib/format';
import { qk } from '../lib/realtime-cache';
import type { AdminRide, Page, RideStatus, VehicleType } from '../lib/types';
import { ridesRoute } from '../router';

type Filter = 'ALL' | RideStatus;
const FILTERS: { value: Filter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'OFFERING', label: 'Offered' },
  { value: 'ACCEPTED', label: 'On the way' },
  { value: 'STARTED', label: 'On trip' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED_BY_RIDER', label: 'Rider cancelled' },
  { value: 'CANCELLED_BY_DRIVER', label: 'Driver cancelled' },
  { value: 'CANCELLED_BY_ADMIN', label: 'Staff cancelled' },
  { value: 'EXPIRED', label: 'No driver' },
];

/** Start of today in India (UTC+5:30), as an ISO time. */
function istDayStart(daysBack = 0) {
  const now = Date.now();
  const ist = new Date(now + 5.5 * 3600_000);
  const midnight = Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate()) - 5.5 * 3600_000;
  return new Date(midnight - daysBack * 86_400_000).toISOString();
}

export function RideTable({ rides, showRider = true }: { rides: AdminRide[]; showRider?: boolean }) {
  const navigate = useNavigate();
  const columns = useMemo(() => {
    const cols: Column<AdminRide>[] = [
      {
        key: 'ride',
        header: 'Ride',
        cell: (r) => (
          <div className="person">
            <VehicleTile type={r.vehicleType} small />
            <div>
              <div className="primary nowrap">{dateTime(r.requestedAt)}</div>
              <div className="secondary">
                {VEHICLE_LABEL[r.vehicleType]} · #{shortId(r.id)}
              </div>
            </div>
          </div>
        ),
      },
    ];
    if (showRider)
      cols.push({
        key: 'rider',
        header: 'Rider',
        cell: (r) => (
          <div>
            <div>{r.rider?.name ?? '—'}</div>
            <div className="secondary nowrap wide-text">{phone(r.rider?.phone)}</div>
          </div>
        ),
      });
    cols.push(
      { key: 'driver', header: 'Driver', className: 'wide-only', cell: (r) => r.driver?.name ?? <span className="faint">—</span> },
      {
        key: 'trip',
        header: 'Trip',
        cell: (r) => (
          <>
            <span className="truncate trip-cell" style={{ display: 'block', maxWidth: 280 }}>
              {r.pickup.address ?? 'Pickup'} → {r.drop.address ?? 'Drop'}
            </span>
            <div className="secondary">{km(r.distanceMeters)}</div>
          </>
        ),
      },
      { key: 'fare', header: 'Fare', className: 'right num nowrap', cell: (r) => <span className="strong">{rupees(r.farePaise)}</span> },
      { key: 'status', header: 'Status', cell: (r) => <RideStatusPill status={r.status} /> },
    );
    return cols;
  }, [showRider]);
  return (
    <DataTable
      label="Rides"
      rows={rides}
      columns={columns}
      rowKey={(r) => r.id}
      onRowClick={(r) => navigate({ to: '/rides/$rideId', params: { rideId: r.id } })}
      card={(r) => (
        <RowCard
          lead={<VehicleTile type={r.vehicleType} small />}
          title={showRider ? (r.rider?.name ?? phone(r.rider?.phone)) : `${r.pickup.address ?? 'Pickup'} → ${r.drop.address ?? 'Drop'}`}
          sub={showRider ? `${dateTime(r.requestedAt)} · ${r.pickup.address ?? 'Pickup'} → ${r.drop.address ?? 'Drop'}` : `${dateTime(r.requestedAt)} · ${r.driver?.name ?? 'no driver'}`}
          side={
            <>
              <span className="num">{rupees(r.farePaise)}</span>
              <RideStatusPill status={r.status} />
            </>
          }
        />
      )}
    />
  );
}

export function RidesPage() {
  const search = ridesRoute.useSearch();
  const navigate = ridesRoute.useNavigate();
  const page = search.page ?? 1;
  const { status, type, period } = search;
  const from = period === 'today' ? istDayStart() : period === 'week' ? istDayStart(6) : undefined;
  const q = useQuery({
    queryKey: [...qk.rides, { status, type, period, page }],
    queryFn: () => api.get<Page<AdminRide>>('/admin/rides', { status, vehicleType: type, from, page, pageSize: 25 }),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHead title="Rides" sub={q.data ? `${q.data.total.toLocaleString('en-IN')} bookings, newest first` : 'Every booking, newest first.'} />
      <div className="toolbar">
        <Chips
          label="Filter by status"
          value={(status ?? 'ALL') as Filter}
          options={FILTERS}
          onChange={(v) => navigate({ search: { ...search, status: v === 'ALL' ? undefined : v, page: undefined } })}
        />
      </div>
      <div className="toolbar">
        <Chips<'ANY' | VehicleType>
          label="Vehicle"
          value={type ?? 'ANY'}
          options={[
            { value: 'ANY', label: 'Every vehicle' },
            { value: 'BIKE', label: 'Bike' },
            { value: 'AUTO', label: 'Auto' },
            { value: 'CAR', label: 'Car' },
          ]}
          onChange={(v) => navigate({ search: { ...search, type: v === 'ANY' ? undefined : v, page: undefined } })}
        />
        <Chips<'all' | 'today' | 'week'>
          label="When"
          value={period ?? 'all'}
          options={[
            { value: 'all', label: 'Any time' },
            { value: 'today', label: 'Today' },
            { value: 'week', label: 'Last 7 days' },
          ]}
          onChange={(v) => navigate({ search: { ...search, period: v === 'all' ? undefined : v, page: undefined } })}
        />
      </div>
      <section className="card">
        {q.isLoading ? (
          <Loading rows={6} />
        ) : q.error ? (
          <div className="card-body">
            <ErrorNote error={q.error} />
          </div>
        ) : !q.data?.items.length ? (
          <Empty title="No rides here" icon={IconRides}>
            Try another filter.
          </Empty>
        ) : (
          <>
            <RideTable rides={q.data.items} />
            <Pager page={page} pageSize={q.data.pageSize} total={q.data.total} onPage={(p) => navigate({ search: { ...search, page: p } })} />
          </>
        )}
      </section>
    </>
  );
}
